import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import Color from 'color';
import {
  ChangeEvent,
  ComponentProps,
  ComponentPropsWithoutRef,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ColorResult, SketchPicker } from 'react-color';
import { WrappedFieldInputProps } from 'redux-form';
import styled, { css } from 'styled-components';

import FormWarning from 'dashboard/components/FormControls/FormWarning';
import { IconAlertCircleStyled } from 'dashboard/components/FormControls/FormWarning/FormWarning';
import Tooltip from 'dashboard/components/Tooltip';
import { COLORS, Z_INDICES } from 'dashboard/styles/variables';
import Input from 'design-system/components/Input';
import inputMixin, { InputSizes } from 'design-system/mixins/input';
import useContainerTargetDetector from 'lib/hooks/useContainerTargetDetector';
import { useEscListener } from 'lib/hooks/useKeyListener';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { isValidColor } from 'lib/utils/formValidation';

const Container = styled('div')`
  display: flex;
  position: relative;
`;

const SwatchSize = {
  sm: `${parseInt(Spacing[3]) - 1}px`,
  md: `${parseInt(Spacing[4]) - 1}px`,
  lg: `${parseInt(Spacing[5]) - 1}px`,
};

const Swatch = styled('button').attrs((props) => ({
  type: 'button',
  style: {
    backgroundColor: props.color,
  },
}))<{ color: string; hasWarning?: boolean; hasError?: boolean; inputSize?: InputSizes }>`
  ${inputMixin};
  width: ${({ theme, inputSize }) =>
    inputSize != null ? SwatchSize[inputSize] : theme.element.height.md};
  height: ${({ theme, inputSize }) =>
    inputSize != null ? SwatchSize[inputSize] : theme.element.height.md};
  ${({ inputSize }) =>
    inputSize != null &&
    css`
      padding: ${SwatchSize[inputSize]};
    `}
  flex-shrink: 0;
  margin-right: ${Spacing[2]};
  background-size: 16px 16px;
  background-position:
    0 0,
    ${Spacing[2]} ${Spacing[2]};
  cursor: pointer;
  ${({ color, hasError, hasWarning, theme }) => {
    if (!color) {
      let darkColor = theme.color.background.secondary;
      if (hasError) {
        darkColor = theme.color.variants.danger.default;
      } else if (hasWarning) {
        darkColor = theme.color.variants.caution.default;
      }

      return css`
        background-image: linear-gradient(
            45deg,
            ${theme.color.background.primary} 25%,
            transparent 25%,
            transparent 75%,
            ${theme.color.background.primary} 75%
          ),
          linear-gradient(
            45deg,
            ${theme.color.background.primary} 25%,
            ${darkColor} 25%,
            ${darkColor} 75%,
            ${theme.color.background.primary} 75%
          );
      `;
    }
  }};
`;

const IconButton = styled('button').attrs(() => ({ type: 'button' }))<{ inputSize?: InputSizes }>`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  color: ${COLORS.gray20};

  height: 100%;
  ${({ inputSize }) =>
    inputSize != null
      ? css`
          padding: ${SwatchSize[inputSize]};
        `
      : css`
          width: 56px;
        `}

  display: flex;
  align-items: center;
  justify-content: center;
  &:hover,
  &:focus {
    cursor: pointer;
    color: ${COLORS.gray50};
  }
  &:active {
    color: ${COLORS.gray100};
  }
`;

const SketchPickerTooltip = styled(Tooltip)`
  box-shadow: none;
  padding: 0;
  margin-left: ${Spacing[2]};
`;

const SketchPickerContainer = styled('div')`
  z-index: ${Z_INDICES.toast};
`;
const StyledInput = styled(Input)`
  align-self: center;
`;

interface ColorWarningProps {
  warning: string;
}

interface ColorTooltipProps {
  modifiers?: ComponentProps<typeof Tooltip>['modifiers'];
  placement?: ComponentProps<typeof Tooltip>['placement'];
  usePortal?: boolean;
}

export type ColorInputProps = ComponentPropsWithoutRef<'input'> &
  Partial<WrappedFieldInputProps> &
  FormControlProps &
  ColorWarningProps &
  ColorTooltipProps & {
    inputSize?: InputSizes;
  };

const ColorInput = ({ defaultValue, ...props }: ColorInputProps) => {
  const {
    name,
    value: currentValue,
    onChange,
    hasError,
    hasWarning,
    warning,
    inputSize,
    usePortal = true,
    disabled,
    ...popoverProps
  } = props;

  const [active, setActive] = useState(false);
  const [pendingColor, setPendingColor] = useState(currentValue);
  const value = currentValue ?? pendingColor;

  // Sync stateful input with external state changes
  useEffect(() => setPendingColor(value), [value]);

  const handlePickColor = useCallback(
    (colorResult: Partial<ColorResult>) => onChange?.(colorResult.hex),
    [onChange],
  );

  const handleInputChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const hex = evt.currentTarget.value;
      setPendingColor(hex);
      if (isValidColor(hex)) {
        handlePickColor({ hex });
      } else if (isValidColor(`#${hex}`)) {
        handlePickColor({ hex: `#${hex}` });
      }
    },
    [handlePickColor],
  );

  const handleInputBlur = useCallback(() => {
    if (!isValidColor(pendingColor)) {
      setPendingColor(value);
    }

    // normalize to full 6-digit hex onblur
    if (isValidColor(pendingColor) && pendingColor.replace('#', '').length === 3) {
      handlePickColor({ hex: new Color(pendingColor).hex() });
    }
  }, [value, pendingColor, handlePickColor]);

  const handleClearInput = useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      setPendingColor(null);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handlePickColor({ hex: null });
    },
    [handlePickColor],
  );

  const containerEl = useRef(null);
  const pickerEl = useRef(null);

  useContainerTargetDetector(() => setActive(false), containerEl, pickerEl);

  useEscListener(() => setActive(false));

  return (
    <div>
      <SketchPickerTooltip
        placement='right-start'
        {...popoverProps}
        usePortal={usePortal}
        hideArrow={true}
        tooltipShown={active}
        tooltip={
          <SketchPickerContainer ref={pickerEl}>
            <SketchPicker
              color={value}
              disableAlpha={true}
              presetColors={[]}
              onChangeComplete={handlePickColor}
            />
          </SketchPickerContainer>
        }
      >
        <Container ref={containerEl} onClick={() => setActive(true)}>
          <Swatch
            color={value}
            hasWarning={hasWarning}
            hasError={hasError}
            tabIndex={-1}
            inputSize={inputSize}
            disabled={disabled}
          />
          <StyledInput
            {...(props as any)}
            onFocus={() => setActive(true)}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            value={pendingColor}
            maxLength={9}
            disabled={disabled}
            size={inputSize}
          />
          {currentValue !== '' && currentValue !== defaultValue && (
            <IconButton
              onClick={handleClearInput}
              inputSize={inputSize}
              disabled={disabled}
              aria-label='Clear color'
            >
              <XIcon />
            </IconButton>
          )}
        </Container>
      </SketchPickerTooltip>
      {!!warning && (
        <FormWarning data-test={`${name}__form-warning`}>
          <IconAlertCircleStyled />
          {warning}
        </FormWarning>
      )}
    </div>
  );
};

export default ColorInput;
